import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Sparkles,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { tenantKeys } from "@/lib/queryKeys";
import {
  getFiscaloConfigRunningForTenant,
  listCaesForTenant,
  startFiscaloConfigForTenant,
  type TenantOnboardingOverview,
} from "@/services/platformTenantsService";

interface Props {
  tenantId: string;
  overview: TenantOnboardingOverview;
}

interface Prereq {
  ok: boolean;
  label: string;
}

/**
 * Root-only one-click trigger for the merged "Configuración Fiscalo en DGI"
 * flow (idemisor_sync → proveedor_software) — the admin-side equivalent of
 * the accountant onboarding's config tile. Preconditions are surfaced as
 * warnings but never block: root can fire the flow regardless (warn-but-allow).
 */
export function TenantFiscaloConfigCard({
  tenantId,
  overview,
}: Props): JSX.Element {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const sawRunningRef = useRef(false);

  const progress = overview.dgiAutoOnboardingProgress ?? {};
  const idemisorDone = Boolean(progress.idemisor_sync);
  const proveedorDone = Boolean(progress.proveedor_software);
  const fiscaloConfigDone = idemisorDone && proveedorDone;

  const { data: caes } = useQuery({
    queryKey: tenantKeys.cae(tenantId),
    queryFn: () => listCaesForTenant(tenantId),
  });

  const prereqs: Prereq[] = [
    { ok: Boolean(overview.planId), label: "Plan asignado" },
    { ok: overview.dgiPortalUserConfirmed, label: "Usuario e-Factura confirmado" },
    { ok: overview.hasDgiCredentials, label: "Certificado digital cargado" },
    { ok: (caes?.length ?? 0) > 0, label: "Al menos un CAE cargado" },
  ];
  const missingPrereqs = prereqs.filter((p) => !p.ok);

  const { data: runningState } = useQuery({
    queryKey: ["tenants", "fiscalo-config-running", tenantId],
    queryFn: () => getFiscaloConfigRunningForTenant(tenantId),
    // Poll once on mount (recovers the spinner after a page refresh mid-run),
    // then every 5s while the automation is in flight.
    refetchInterval: (query) =>
      !fiscaloConfigDone && (saving || query.state.data?.running)
        ? 5_000
        : false,
    enabled: !fiscaloConfigDone,
  });
  const running = runningState?.running ?? false;

  // Adopt an automation that's already in flight (e.g. started in another tab
  // or before a page refresh) so the spinner + completion detection kick in.
  useEffect(() => {
    if (running && !saving && !fiscaloConfigDone) {
      setSaving(true);
      sawRunningRef.current = true;
    }
  }, [running, saving, fiscaloConfigDone]);

  // Refetch the overview while the automation runs so the done-state flips on
  // its own when the webhook stamps the steps.
  useEffect(() => {
    if (!saving) return;
    void queryClient.invalidateQueries({
      queryKey: tenantKeys.overview(tenantId),
    });
  }, [running, saving, tenantId, queryClient]);

  // Completion / stall detection.
  useEffect(() => {
    if (!saving) return;
    if (fiscaloConfigDone) {
      setSaving(false);
      sawRunningRef.current = false;
      toast.success("Fiscalo configurado en DGI.");
      return;
    }
    if (running) {
      sawRunningRef.current = true;
      return;
    }
    // Was running, now stopped, but not done → the worker likely died.
    if (sawRunningRef.current) {
      setSaving(false);
      sawRunningRef.current = false;
      toast.error(
        "La configuración se interrumpió antes de terminar. Revisá el auto-flow y reintentá.",
      );
    }
  }, [running, saving, fiscaloConfigDone]);

  const triggerMutation = useMutation({
    mutationFn: () => startFiscaloConfigForTenant(tenantId),
    onSuccess: (res) => {
      if (!res.accepted) {
        toast.error(
          res.message ?? "No pudimos iniciar la configuración. Probá de nuevo.",
        );
        return;
      }
      setSaving(true);
      sawRunningRef.current = false;
      toast.success("Configurando en DGI… Tardará 1-3 minutos.");
    },
    onError: () => {
      toast.error("No pudimos iniciar la configuración. Probá de nuevo.");
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-4 w-4" />
          Configuración Fiscalo en DGI
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Sincroniza el IdEmisor y registra a Fiscalo como proveedor de software
          en el portal de DGI del tenant — igual que el onboarding del contador,
          en un clic.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {fiscaloConfigDone ? (
          <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3">
            <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
            <div className="text-sm">
              <div className="font-medium text-emerald-900">
                Fiscalo ya está configurado
              </div>
              <div className="text-xs text-emerald-800">
                IdEmisor sincronizado y Fiscalo registrado como proveedor de
                software.
              </div>
            </div>
          </div>
        ) : saving && running ? (
          <div className="flex items-start gap-3 rounded-md border border-border bg-muted/30 p-3">
            <Loader2 className="mt-0.5 h-4 w-4 animate-spin text-muted-foreground" />
            <div className="text-sm">
              <div className="font-medium">Configurando en DGI…</div>
              <div className="text-xs text-muted-foreground">
                {runningState?.step === "proveedor_software"
                  ? "Registrando a Fiscalo como proveedor de software."
                  : "Sincronizando el IdEmisor desde el portal de DGI."}{" "}
                Esto puede tardar hasta 5 minutos.
              </div>
            </div>
          </div>
        ) : (
          <>
            {missingPrereqs.length > 0 && (
              <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600" />
                <div className="text-sm">
                  <div className="font-medium text-amber-900">
                    Faltan prerequisitos
                  </div>
                  <ul className="mt-1 list-disc pl-4 text-xs text-amber-800">
                    {missingPrereqs.map((p) => (
                      <li key={p.label}>{p.label}</li>
                    ))}
                  </ul>
                  <div className="mt-1 text-xs text-amber-800">
                    Podés ejecutar igual, pero la automatización puede fallar si
                    falta algo en el portal de DGI.
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2 text-xs text-muted-foreground">
              <span>
                IdEmisor:{" "}
                {idemisorDone ? (
                  <span className="text-emerald-700">sincronizado</span>
                ) : (
                  <span>pendiente</span>
                )}
              </span>
              <span>
                Proveedor de software:{" "}
                {proveedorDone ? (
                  <span className="text-emerald-700">registrado</span>
                ) : (
                  <span>pendiente</span>
                )}
              </span>
            </div>

            <Button
              type="button"
              onClick={() => {
                if (!triggerMutation.isPending && !saving) {
                  triggerMutation.mutate();
                }
              }}
              disabled={triggerMutation.isPending || saving}
            >
              {triggerMutation.isPending || saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              Configurar Fiscalo en DGI
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
